/**
 * @fileoverview Pure helpers for deriving the assessed view of the curriculum
 * tree. The backend marks lv4 leaves with a rounded average; "assessed" means a
 * leaf with a positive average. Per the design, only assessed branches/leaves
 * are shown under each lv1, and the lv1 badge counts assessed leaves.
 */

import type { ReportNode } from '../types'

export function isAssessedLeaf(node: ReportNode): boolean {
  return node.children.length === 0 && node.average != null && node.average > 0
}

/** Counts assessed leaves anywhere under the given nodes (lv1 badge value). */
export function countAssessedLeaves(nodes: ReportNode[]): number {
  return nodes.reduce((total, node) => {
    if (node.children.length === 0) return total + (isAssessedLeaf(node) ? 1 : 0)
    return total + countAssessedLeaves(node.children)
  }, 0)
}

/** Keeps only branches/leaves that contain at least one assessed leaf. */
export function pruneToAssessed(nodes: ReportNode[]): ReportNode[] {
  return nodes.reduce<ReportNode[]>((kept, node) => {
    if (node.children.length === 0) {
      if (isAssessedLeaf(node)) kept.push(node)
      return kept
    }
    const children = pruneToAssessed(node.children)
    if (children.length > 0) kept.push({ ...node, children })
    return kept
  }, [])
}
